import { AxiosResponse } from "axios";

import {
  IAdminFirmwareListResponse,
  IFirmwareFormValues,
} from "../../@types/admin/AdminFirmware";
import { api } from "../api";
import { IFileUploadRequest } from "./fileTransferService";

/**
 * Firmware admin page (EN-5166), migrated from the Retool Firmware app.
 *
 * The ticket lists a new list endpoint as backend work; `GET /Retool/Firmware`
 * already existed and is used as-is. What it was missing — `publish` — was
 * added to `FirmwareDto` rather than building a second endpoint over the same
 * table.
 */
export const firmwareService = {
  /** Every firmware record. Note the `{ data: [...] }` envelope. */
  async getFirmware(): Promise<AxiosResponse<IAdminFirmwareListResponse>> {
    return api.get<IAdminFirmwareListResponse>("/Retool/Firmware");
  },
};

/**
 * Multipart body for create/update, as a `useFileUpload` request.
 *
 * `[FromForm] CreateOrUpdateFirmwareRequestDto` binds PascalCase names, and
 * `Generations` is a single **pipe-separated string** — the API splits on
 * "|". Retool did the same join in its query body; sending an array here
 * binds as an empty list and silently wipes a firmware's generations.
 *
 * `Binary` is omitted when no new file is picked, so an update keeps the
 * stored binary (`MapToEntity` falls back to the existing value).
 */
export function buildFirmwareUploadRequest(
  values: IFirmwareFormValues,
  firmwareId?: number
): IFileUploadRequest {
  const formData = new FormData();
  formData.append("Device", values.device);
  formData.append("Version", values.version);
  formData.append("ChangeLog", values.changeLog);
  formData.append("Publish", String(values.publish));
  formData.append("Generations", values.generations.join("|"));
  formData.append("Platform", values.platform);
  if (values.binary) formData.append("Binary", values.binary);

  return firmwareId === undefined
    ? { url: "/Retool/Firmware", method: "POST", formData }
    : { url: `/Retool/Firmware/${firmwareId}`, method: "PUT", formData };
}
